import type { BlockDefinition, BlockVersionMeta } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import { fitsInMm } from '../canvas/fit-mm'
import { formatMmCeil, formatMmFloor } from '../canvas/format-mm'
import { buildStalenessReport } from './block-instance-ops'

interface VersionPickerPopoverProps {
  hostBox: { widthMm: number; heightMm: number }
  versions: BlockVersionMeta[]
  draft: { widthMm: number; heightMm: number } | undefined
  currentVersion: number
  onSwap: (version: number) => void
  onSwapToDraft: () => void
  onDetach: () => void
  onCancel: () => void
  /**
   * Loaded block definitions keyed by version number. Each NON-current version
   * row whose canonical variable set differs from the current version's def
   * shows an added/removed diff (def-vs-def — instance props are sparse and
   * would yield false "+new" for defaulted variables). Rows whose def — or the
   * current version's def — is absent from this map show no diff line.
   */
  versionDefs?: Map<number, BlockDefinition>
}

/** The popover's `t`, threaded into the free helpers below (they run outside
 *  the component body and so cannot call `useTranslation` themselves). */
type Translate = ReturnType<typeof useTranslation>['t']

function fitLabel(
  t: Translate,
  host: { widthMm: number; heightMm: number },
  dims: { widthMm: number; heightMm: number }
): string {
  return fitsInMm(host, dims)
    ? t('templateDesigner.versionPicker.fits')
    : t('templateDesigner.versionPicker.needsFit', {
        // A requirement on a REFUSED row: ceil, so it always reads larger than
        // the floored host-zone line above it (#126).
        width: formatMmCeil(dims.widthMm),
        height: formatMmCeil(dims.heightMm),
      })
}

/**
 * Build a human-readable diff line for the variable-set change when swapping
 * from the current version's def to a target version's def. Returns
 * `undefined` when there's no diff (identical canonical sets) or when either
 * def isn't available.
 */
function buildVarDiffLine(
  t: Translate,
  currentDef: BlockDefinition | undefined,
  targetDef: BlockDefinition | undefined,
): string | undefined {
  const { addedVariables: added, removedVariables: removed } = buildStalenessReport(
    currentDef,
    targetDef,
  )
  if (added.length === 0 && removed.length === 0) return undefined
  const parts: string[] = []
  // Real i18next plurals, not the old inline `n === 1 ? '' : 's'` — English
  // morphology baked into one string reads wrong in es/el (#154).
  if (added.length > 0) {
    parts.push(t('templateDesigner.versionPicker.addedVariables', { count: added.length }))
  }
  if (removed.length > 0) {
    parts.push(
      t('templateDesigner.versionPicker.removedVariables', {
        count: removed.length,
        names: removed.join(', '),
      }),
    )
  }
  return parts.join(', ')
}

export function VersionPickerPopover({
  hostBox,
  versions,
  draft,
  currentVersion,
  onSwap,
  onSwapToDraft,
  onDetach,
  onCancel,
  versionDefs,
}: VersionPickerPopoverProps) {
  const { t } = useTranslation()
  const sorted = [...versions].sort((a, b) => b.version - a.version)
  // The diff baseline: the def of the version the instance is currently baked at.
  const currentDef = versionDefs?.get(currentVersion)

  return (
    <div
      data-testid="version-picker-popover"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl dark:bg-stone-900">
        <h3 className="mb-2 text-sm font-semibold">
          {t('templateDesigner.versionPicker.title')}
        </h3>
        {/* #126: hostBox carries LAID-OUT mm straight off the split arithmetic,
            so React's String() printed "55.00000000000001×40 mm" for the same
            zone the instance table renders as "55 × 40". The rows compare with
            fitsInMm(), whose 1e-6 tolerance absorbs exactly that residue: a zone
            printed here as "45" admits a 45mm version instead of disabling its
            row under an un-actionable "✗ needs ≥ 45×30 mm". No wider than
            residue, though — a real 0.04mm shortfall still rejects, and this
            line FLOORS so that shortfall is visible: a 59.96mm zone reads
            "59.9", under which "✗ needs ≥ 60×30 mm" is self-explanatory. */}
        <p className="mb-3 text-[11px] text-stone-500">
          {t('templateDesigner.versionPicker.hostZone', {
            width: formatMmFloor(hostBox.widthMm),
            height: formatMmFloor(hostBox.heightMm),
          })}
        </p>
        <ul className="space-y-1.5">
          {draft && (
            <li
              data-testid="version-row-draft"
              className="rounded border border-amber-400 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-950"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold">
                    {t('templateDesigner.versionPicker.draft')}
                  </span>
                  <span className="ml-2 text-[10px] text-stone-500">
                    {fitLabel(t, hostBox, draft)}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="version-row-draft-action"
                  disabled={!fitsInMm(hostBox, draft)}
                  onClick={onSwapToDraft}
                  className="rounded bg-sky-600 px-2 py-0.5 text-[11px] text-white disabled:opacity-40 hover:enabled:bg-sky-700"
                >
                  {t('templateDesigner.versionPicker.promoteAndSwap')}
                </button>
              </div>
            </li>
          )}
          {sorted.map(v => {
            const fits = fitsInMm(hostBox, v)
            const isCurrent = v.version === currentVersion
            // Never diff the current row against itself.
            const varDiffLine = isCurrent
              ? undefined
              : buildVarDiffLine(t, currentDef, versionDefs?.get(v.version))
            return (
              <li
                key={v.version}
                data-testid={`version-row-${v.version}`}
                className="rounded border border-stone-200 bg-stone-50 p-2 dark:border-stone-800 dark:bg-stone-950"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold">v{v.version}</span>
                    {isCurrent && (
                      <span className="ml-2 text-[10px] text-stone-500">
                        {t('templateDesigner.versionPicker.current')}
                      </span>
                    )}
                    <span className="ml-2 text-[10px] text-stone-500">
                      {fitLabel(t, hostBox, v)}
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid={`version-row-${v.version}-action`}
                    disabled={!fits || isCurrent}
                    onClick={() => onSwap(v.version)}
                    className="rounded border border-stone-300 px-2 py-0.5 text-[11px] text-stone-700 disabled:opacity-40 hover:enabled:bg-stone-100 dark:border-stone-700 dark:text-stone-300"
                  >
                    {t('templateDesigner.versionPicker.swap')}
                  </button>
                </div>
                {varDiffLine && (
                  <p
                    data-testid={`version-row-${v.version}-var-diff`}
                    className="mt-0.5 text-[10px] text-violet-700 dark:text-violet-400"
                  >
                    {varDiffLine}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onDetach}
            className="rounded border border-rose-300 px-3 py-1 text-xs text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300"
          >
            {t('common.detach')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}
